import { mediaQueryLarge, requestIdleCallback } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 * v232: denser grid applies sync (no product-grid VT idle lag).
 * Grid density radios live in the facets bar; listen on document for change/click.
 */
export default class ResultsList extends PaginatedList {
  /** @type {AbortController | null} */
  #layoutAbort = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#bindLayoutControls();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#layoutAbort?.abort();
    this.#layoutAbort = null;
  }

  /**
   * Bind layout controls wherever they sit in the document (facets form).
   */
  #bindLayoutControls() {
    this.#layoutAbort?.abort();
    this.#layoutAbort = new AbortController();
    const { signal } = this.#layoutAbort;

    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name !== 'grid' && target.name !== 'grid-mobile') return;
        this.#animateLayoutChange(target.value);
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        const el = event.target;
        if (!(el instanceof Element)) return;
        const option = el.closest('.column-options__option');
        if (!option) return;
        const input = option.querySelector(
          'input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]'
        );
        if (!(input instanceof HTMLInputElement)) return;
        requestAnimationFrame(() => {
          if (input.checked) this.#animateLayoutChange(input.value);
        });
      },
      { signal }
    );
  }

  /**
   * Declarative on:change="results-list/updateLayout" path.
   * @param {Event} event
   */
  updateLayout(event) {
    const target = event?.target ?? event?.detail?.target ?? event;
    if (!(target instanceof HTMLInputElement)) {
      const input =
        (event?.currentTarget instanceof HTMLInputElement && event.currentTarget) ||
        (event?.target instanceof Element && event.target.closest?.('input[type="radio"]'));
      if (!(input instanceof HTMLInputElement)) return;
      this.#animateLayoutChange(input.value);
      return;
    }

    this.#animateLayoutChange(target.value);
  }

  /**
   * @param {string} value
   */
  #animateLayoutChange = (value) => {
    // ekimall v232: apply denser/default layout synchronously.
    // Previous path awaited startViewTransition(..., ['product-grid']), whose
    // type setup uses requestIdleCallback and delayed setAttribute by seconds
    // (especially while a sort remorph VT was also queued).
    this.#setLayout(value);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * @param {string} value
   */
  #setLayout(value) {
    const grid =
      this.refs?.grid ||
      this.querySelector('[ref="grid"]') ||
      this.querySelector('.product-grid') ||
      document.querySelector('results-list .product-grid, .product-grid');
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    if (value === 'zoom-out') {
      grid.style.setProperty(
        'grid-template-columns',
        'repeat(auto-fill, minmax(6.25rem, 1fr))',
        'important'
      );
    } else if (value === 'mobile-single') {
      grid.style.setProperty('grid-template-columns', '1fr', 'important');
    } else {
      grid.style.removeProperty('grid-template-columns');
    }
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    // Radios are in the facets bar, not inside results-list
    const targetElement = event.matches
      ? document.querySelector('[data-grid-layout="desktop-default-option"]')
      : document.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
